import React, { ReactNode } from "react";
import { Button, Tag, cn } from "@jem-open/jem-ui";

export interface InheritedFieldProps {
  /** Visual label displayed above the value. */
  label: string;
  /** The input control rendered as the effective-value editor. */
  children: ReactNode;
  /** Where this value came from. Drives the source tag + helper text. */
  source: "site" | "override";
  /** The Site default the field would inherit if the override were cleared. Required when source === "override". */
  siteDefaultLabel?: string;
  /** Name of the parent site for the "From Site: X" label. */
  siteName?: string;
  /** Click handler for the inline "Revert to Site default" link. Only fires when source === "override". */
  onRevert?: () => void;
  /** Optional helper text shown below the field. */
  helperText?: string;
  /** Optional class override. */
  className?: string;
}

/**
 * One inheritable Roster field — the workhorse of the unified config page.
 *
 * Layout (top → bottom):
 *   1. Label row with a subtle Tag indicating source ("From Site" or "Overridden")
 *   2. The input control (passed as children)
 *   3. Helper-text row containing either "From Site: <value>" (when inheriting)
 *      or "Will return to Site value of {X} · Revert to Site default"
 *      (when overridden, with the revert action inline)
 *
 * The Tag is in addition to (not instead of) the text label — accessibility
 * requires more than colour to communicate state. Research recommendation
 * (BambooHR + GitHub hybrid pattern).
 */
export const InheritedField: React.FC<InheritedFieldProps> = ({
  label,
  children,
  source,
  siteDefaultLabel,
  siteName,
  onRevert,
  helperText,
  className,
}) => {
  const isInherited = source === "site";

  return (
    <div className={cn("space-y-xxs", className)}>
      <div className="flex items-center gap-xs">
        <span className="text-sm font-semibold text-greyscale-text-title">
          {label}
        </span>
        {!isInherited && (
          <Tag variant="processing" size="sm">
            Custom
          </Tag>
        )}
      </div>

      {children}

      <div className="flex flex-wrap items-center gap-xs text-xs text-greyscale-text-caption">
        {isInherited && siteDefaultLabel && (
          <span>
            Same as {siteName ?? "Site"}: <strong>{siteDefaultLabel}</strong>
          </span>
        )}
        {!isInherited && siteDefaultLabel && (
          <>
            <span>
              {siteName ?? "Site"} value is <strong>{siteDefaultLabel}</strong>
            </span>
            {onRevert && (
              <>
                <span aria-hidden="true">·</span>
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={onRevert}
                >
                  Use {siteName ?? "Site"} value
                </Button>
              </>
            )}
          </>
        )}
        {helperText && (
          <>
            {(isInherited || siteDefaultLabel) && (
              <span aria-hidden="true">·</span>
            )}
            <span>{helperText}</span>
          </>
        )}
      </div>
    </div>
  );
};

export default InheritedField;
